import * as R from 'ramda';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useEffect, useCallback } from 'react';
// components
import { FormFooter } from '../../../components/form-footer';
// features
import { makeSelectItemListGuids } from '../../../features/compensation-group/selectors';
// forms
import { Fieldset2 } from '../../../forms';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hooks
import { useModalAndLoaderActions } from '../../../hooks';
// ui
import { MainActionButton } from '../../../ui';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature fleet-profile
import { createCompensationGroupRequest } from '../actions';
import { makeSelectPrimaryObjectGuid, makeSelectEntityBranchGuid } from '../selectors';
//////////////////////////////////////////////////

const fields = [
  {
    isMulti: true,
    type: 'reactSelect',
    options: 'compensationGroups',
    fieldName: 'compensationGroupGuids',
    inputWrapperStyles: { mb: 15, mt: 11, width: 400 },
    label: ['titles:compensation-group', 'Compensation Group'],
  },
];

const useGetAvailableCompensationGroups = ({ openLoader, closeLoader }) => {
  const [compensationGroups, setCompensationGroups] = useState([]);

  const branchGuid = useSelector(makeSelectEntityBranchGuid());

  useEffect(() => {
    const handleGetAvailableCompensationGroups = async () => {
      openLoader();

      const options = {
        params: {
          [GC.BRANCH_GUID]: branchGuid,
        },
      };

      const res = await sendRequest('get', endpointsMap.compensationGroupListAvailable, options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        setCompensationGroups(G.mapNameGuidObjectPropsToLabelValueObject(R.or(data, [])));
      } else {
        G.handleFailResponseSimple(res, true, 'handleGetAvailableCompensationGroups fail');
      }

      closeLoader();
    };

    handleGetAvailableCompensationGroups();
  }, []);

  return compensationGroups;
};

const SelectCompensationGroupForm = ({ openLoader, closeLoader, submitAction, initialValues }) => {
  const formik = useFormik({
    initialValues,
    onSubmit: submitAction,
  });

  const { handleSubmit } = formik;

  const compensationGroups = useGetAvailableCompensationGroups({ openLoader, closeLoader });

  return (
    <form onSubmit={handleSubmit}>
      <Fieldset2 {...formik} fields={fields} compensationGroups={compensationGroups} />
      <FormFooter />
    </form>
  );
};

const SelectCompensationGroup = ({ isVendor, isReadOnlyMode }) => {
  const dispatch = useDispatch();

  const compensationGroupGuids = useSelector(makeSelectItemListGuids);
  const primaryObjectGuid = useSelector(makeSelectPrimaryObjectGuid());
  const primaryObjectGuidKey = G.ifElse(isVendor, GC.FIELD_FLEET_VENDOR_GUID, GC.FIELD_DRIVER_GUID);

  const { openModal, openLoader, closeLoader } = useModalAndLoaderActions();

  const handleSelectCompensationGroup = useCallback(() => {
    const initialValues = {
      [primaryObjectGuidKey]: primaryObjectGuid,
      compensationGroupGuids: R.or(compensationGroupGuids, []),
    };

    const component = (
      <SelectCompensationGroupForm
        openLoader={openLoader}
        closeLoader={closeLoader}
        initialValues={initialValues}
        submitAction={(values) => dispatch(createCompensationGroupRequest(values))}
      />
    );

    const modal = {
      p: 15,
      component,
      options: {
        title: G.getWindowLocale('titles:compensation-group', 'Compensation Group'),
      },
    };

    openModal(modal);
  }, [compensationGroupGuids]);

  if (isReadOnlyMode) return null;

  return (
    <MainActionButton ml={20} mb='3px' onClick={handleSelectCompensationGroup}>
      {G.getWindowLocale('actions:add-remove-compensation-group', 'Add/Remove Compensation Group')}
    </MainActionButton>
  );
};

export {
  SelectCompensationGroup,
};
